/*
 * Quizzes are graded in the browser for now. Every generated question ships
 * with its answer key, so a submission only records which option the learner
 * picked and the score is worked out from that each time the page renders.
 */

/**
 * Returns a question's options as [key, text] pairs ordered by their letter.
 * @param {Object} question A quiz question
 * @returns {Array<Array<string>>} The ordered option entries
 */
export const getOptionEntries = (question) =>
  Object.entries(question?.options ?? {}).sort(([a], [b]) => a.localeCompare(b));

/**
 * Grades one question against its answer key.
 * @param {Object} question A quiz question
 * @param {String} selectedKey The option key the learner picked, or null
 * @returns {Object} The result for that question
 */
const gradeQuestion = (question, selectedKey) => ({
  question,
  selectedKey,
  isAnswered: selectedKey !== null,
  isCorrect: selectedKey !== null && selectedKey === question.answer,
});

/**
 * Grades a set of responses against the questions' answer keys.
 * @param {Array<Object>} questions The quiz questions
 * @param {Object} responses A map of question ID to the selected option key
 * @returns {Object} The per-question results plus the overall score
 */
export const gradeQuiz = (questions = [], responses = {}) => {
  const results = questions.map((question) =>
    gradeQuestion(question, responses[question.id] ?? null),
  );

  const total = results.length;
  const correctCount = results.filter((result) => result.isCorrect).length;
  const percentage = total === 0 ? 0 : Math.round((correctCount / total) * 100);

  return { results, total, correctCount, percentage };
};

/**
 * Short answers are laid out two per row; long ones stack full width. True/False
 * questions fall out of this naturally, since "True" and "False" are short.
 * @param {Array<Array<string>>} optionEntries The question's ordered options
 * @param {Number} maxLength The longest an answer can be and still count as short
 * @returns {Boolean} True when the options should sit side by side
 */
export const areOptionsShort = (optionEntries, maxLength) =>
  optionEntries.length > 0 && optionEntries.every(([, text]) => (text ?? '').length <= maxLength);

const TRUE_FALSE_ANSWERS = ['true', 'false'];

const isTrueAnswer = (text) => (text ?? '').trim().toLowerCase() === TRUE_FALSE_ANSWERS[0];

/**
 * A True/False question's options with True first, whichever letters they happen
 * to be stored under. Anything else is left in the order it arrived.
 * @param {Array<Array<string>>} optionEntries The question's ordered options
 * @returns {Array<Array<string>>} The entries, True first
 */
export const orderTrueFirst = (optionEntries) =>
  [...optionEntries].sort(([, a], [, b]) => Number(isTrueAnswer(b)) - Number(isTrueAnswer(a)));

/**
 * True/False questions get their own treatment: two tall boxes with the answer
 * centred and no A./B. prefix, since the words speak for themselves.
 * @param {Array<Array<string>>} optionEntries The question's ordered options
 * @returns {Boolean} True when the options are exactly True and False
 */
export const isTrueFalseQuestion = (optionEntries) =>
  optionEntries.length === 2 &&
  optionEntries.every(([, text]) => TRUE_FALSE_ANSWERS.includes((text ?? '').trim().toLowerCase()));

/**
 * Picks the modifier class for one question in the rail or the phone's question
 * list: how it went once graded, or whether it has been answered before that.
 * @param {Object} result One entry from gradeQuiz
 * @param {Boolean} isGraded Whether the quiz has been submitted
 * @returns {String} The class name, or an empty string
 */
export const getQuestionStateClass = ({ isAnswered, isCorrect }, isGraded) => {
  if (isGraded) return isCorrect ? 'correct' : 'incorrect';
  return isAnswered ? 'answered' : '';
};

/**
 * Returns the headline shown next to the score ring on the results page.
 * @param {Number} percentage The percentage of questions answered correctly
 * @returns {String} The congratulatory or encouraging message
 */
export const getScoreMessage = (percentage) => {
  if (percentage === 100) return 'Congratulations! You got them all right! 🎉';
  if (percentage >= 80) return 'Nice work! You are almost there.';
  if (percentage >= 50) return 'Good start — review the questions you missed.';
  return 'Keep practicing. Review the answers below and try again.';
};
